import { authFetch } from "lib/api/authFetch";
import { BACKEND_URL } from "lib/utils/baseUrl";

function buildCalendarQuery(options = {}) {
  const params = new URLSearchParams();

  if (options.organizationId) {
    params.set("organization_id", options.organizationId);
  }
  if (options.from) {
    params.set("from", options.from);
  }
  if (options.to) {
    params.set("to", options.to);
  }
  if (options.source) {
    params.set("source", options.source);
  }

  const query = params.toString();
  return query ? `?${query}` : "";
}

async function parseJsonResponse(response) {
  const payload = await response.json().catch(() => ({}));
  return {
    success: response.ok && payload?.success === true,
    message: payload?.message ?? "",
    data: payload?.data ?? null,
    code: payload?.code ?? "",
  };
}

export async function getOrganizationCalendarSchedules(options = {}) {
  try {
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar${buildCalendarQuery(options)}`,
      {
        cache: "no-store",
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error("[getOrganizationCalendarSchedules] failed:", error);
    return {
      success: false,
      message: "조직 일정을 불러오지 못했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function getOrganizationCalendarMyWorkspace(options = {}) {
  try {
    const params = new URLSearchParams();
    if (options.organizationId) {
      params.set("organization_id", options.organizationId);
    }
    if (options.limit) {
      params.set("limit", String(options.limit));
    }

    const query = params.toString();
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar/my-workspace${
        query ? `?${query}` : ""
      }`,
      {
        cache: "no-store",
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error("[getOrganizationCalendarMyWorkspace] failed:", error);
    return {
      success: false,
      message: "내 일정과 업무 요청을 불러오지 못했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function getOrganizationCalendarTaskAssignees() {
  try {
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar/task-assignees`,
      {
        cache: "no-store",
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error("[getOrganizationCalendarTaskAssignees] failed:", error);
    return {
      success: false,
      message: "업무 요청 대상자를 불러오지 못했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function createOrganizationCalendarSchedule(payload) {
  try {
    const response = await authFetch(`${BACKEND_URL}/organization/calendar`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    return parseJsonResponse(response);
  } catch (error) {
    console.error("[createOrganizationCalendarSchedule] failed:", error);
    return {
      success: false,
      message: "조직 일정 등록 중 오류가 발생했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function updateOrganizationCalendarSchedule(scheduleId, payload) {
  try {
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar/${encodeURIComponent(scheduleId)}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error("[updateOrganizationCalendarSchedule] failed:", error);
    return {
      success: false,
      message: "조직 일정 수정 중 오류가 발생했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function getOrganizationCalendarScheduleHistory(
  scheduleId,
  options = {},
) {
  try {
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar/${encodeURIComponent(
        scheduleId,
      )}/history${buildCalendarQuery(options)}`,
      {
        cache: "no-store",
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error("[getOrganizationCalendarScheduleHistory] failed:", error);
    return {
      success: false,
      message: "일정 작업 내용 이력을 불러오지 못했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function deleteOrganizationCalendarScheduleHistory(
  scheduleId,
  historyId,
  options = {},
) {
  try {
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar/${encodeURIComponent(
        scheduleId,
      )}/history/${encodeURIComponent(historyId)}${buildCalendarQuery(options)}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error("[deleteOrganizationCalendarScheduleHistory] failed:", error);
    return {
      success: false,
      message: "일정 이력을 삭제하지 못했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function getOrganizationCalendarScheduleComments(
  scheduleId,
  options = {},
) {
  try {
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar/${encodeURIComponent(
        scheduleId,
      )}/comments${buildCalendarQuery(options)}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error("[getOrganizationCalendarScheduleComments] failed:", error);
    return {
      success: false,
      message: "일정 댓글을 불러오지 못했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function createOrganizationCalendarScheduleComment(
  scheduleId,
  payload,
) {
  try {
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar/${encodeURIComponent(
        scheduleId,
      )}/comments`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error("[createOrganizationCalendarScheduleComment] failed:", error);
    return {
      success: false,
      message: "일정 댓글 등록 중 오류가 발생했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function updateOrganizationCalendarScheduleComment(
  scheduleId,
  commentId,
  payload,
) {
  try {
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar/${encodeURIComponent(
        scheduleId,
      )}/comments/${encodeURIComponent(commentId)}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error("[updateOrganizationCalendarScheduleComment] failed:", error);
    return {
      success: false,
      message: "일정 댓글 수정 중 오류가 발생했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function deleteOrganizationCalendarScheduleComment(
  scheduleId,
  commentId,
  options = {},
) {
  try {
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar/${encodeURIComponent(
        scheduleId,
      )}/comments/${encodeURIComponent(commentId)}${buildCalendarQuery(
        options,
      )}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error("[deleteOrganizationCalendarScheduleComment] failed:", error);
    return {
      success: false,
      message: "일정 댓글 삭제 중 오류가 발생했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function getOrganizationCalendarCommentNotifications() {
  try {
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar/comment-notifications`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error(
      "[getOrganizationCalendarCommentNotifications] failed:",
      error,
    );
    return {
      success: false,
      message: "일정 댓글 알림을 불러오지 못했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function markOrganizationCalendarCommentNotificationRead(
  notificationId,
) {
  try {
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar/comment-notifications/${encodeURIComponent(
        notificationId,
      )}/read`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error(
      "[markOrganizationCalendarCommentNotificationRead] failed:",
      error,
    );
    return {
      success: false,
      message: "일정 댓글 알림 읽음 처리 중 오류가 발생했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function getOrganizationCalendarTaskNotifications() {
  try {
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar/task-notifications`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error("[getOrganizationCalendarTaskNotifications] failed:", error);
    return {
      success: false,
      message: "업무 요청 알림을 불러오지 못했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function markOrganizationCalendarTaskNotificationRead(
  notificationId,
) {
  try {
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar/task-notifications/${encodeURIComponent(
        notificationId,
      )}/read`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error(
      "[markOrganizationCalendarTaskNotificationRead] failed:",
      error,
    );
    return {
      success: false,
      message: "업무 요청 알림 읽음 처리 중 오류가 발생했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function respondOrganizationCalendarTaskRequest(
  scheduleId,
  payload,
) {
  try {
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar/${encodeURIComponent(
        scheduleId,
      )}/task-response`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error("[respondOrganizationCalendarTaskRequest] failed:", error);
    return {
      success: false,
      message: "업무 요청 처리 중 오류가 발생했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}

export async function deleteOrganizationCalendarSchedule(scheduleId, options = {}) {
  try {
    const response = await authFetch(
      `${BACKEND_URL}/organization/calendar/${encodeURIComponent(
        scheduleId,
      )}${buildCalendarQuery(options)}`,
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return parseJsonResponse(response);
  } catch (error) {
    console.error("[deleteOrganizationCalendarSchedule] failed:", error);
    return {
      success: false,
      message: "조직 일정 삭제 중 오류가 발생했습니다.",
      data: null,
      code: "NETWORK_ERROR",
    };
  }
}
